type CountryLike = {
  code: string;
  name: string;
};

export type LocalizedCountryOption = {
  value: string;
  label: string;
  alpha3: string;
};

/*
 * The country reference API uses ISO 3166-1 alpha-3 codes for catalog and
 * market data, while tenant.country (and Intl.DisplayNames) use alpha-2.
 * Keep this conversion at the UI boundary so the API's existing consumers
 * can continue to use alpha-3 codes.
 */
const ALPHA3_CODES =
  "AUT ALB AND ARM AZE BLR BEL BGR BIH VAT GBR HUN DEU GGY GIB GRC GEO DNK JEY IRL ISL ESP ITA KAZ CYP KGZ LVA LTU LIE LUX MLT MDA MCO NLD NOR IMN POL PRT RUS ROU SMR MKD SRB SVK SVN TJK TKM TUR UZB UKR FRO FIN FRA HRV MNE CZE CHE SWE SJM EST AUS ASM AFG BGD BHR BRN BTN VUT VNM HKG GUM ISR IND IDN JOR IRQ IRN YEM KHM QAT KIR CHN PRK KOR KWT LAO LBN MAC MYS MDV MHL FSM MNG MMR NRU NPL NIU NZL NCL ARE OMN COK PAK PLW PSE PNG SAU MNP SGP SYR SLB THA TWN TLS TKL TON TUV WLF PHL LKA JPN AIA ATG ARG ABW BHS BRB BLZ BMU BOL BES BRA VGB VIR HTI GUY GLP GTM GUF HND GRD GRL DMA DOM CAN COL CRI CUB CUW MTQ MEX MSR NIC CYM PAN PRY PER PRI SLV BLM MAF SPM VCT KNA LCA SXM SUR USA TTO URY FLK CHL ECU JAM DZA AGO BEN BWA BFA BDI GAB GMB GHA GIN GNB DJI COD EGY ZMB ZWE CPV CMR KEN COM CIV LSO LBR LBY MUS MRT MDG MWI MLI MAR MOZ NAM NER NGA COG REU RWA STP SYC SEN SOM SDN SLE TZA TGO TUN UGA CAF TCD GNQ ERI SWZ ETH ZAF SSD ATA BVT CXR HMD ATF".split(
    " ",
  );
const ALPHA2_CODES =
  "AT AL AD AM AZ BY BE BG BA VA GB HU DE GG GI GR GE DK JE IE IS ES IT KZ CY KG LV LT LI LU MT MD MC NL NO IM PL PT RU RO SM MK RS SK SI TJ TM TR UZ UA FO FI FR HR ME CZ CH SE SJ EE AU AS AF BD BH BN BT VU VN HK GU IL IN ID JO IQ IR YE KH QA KI CN KP KR KW LA LB MO MY MV MH FM MN MM NR NP NU NZ NC AE OM CK PK PW PS PG SA MP SG SY SB TH TW TL TK TO TV WF PH LK JP AI AG AR AW BS BB BZ BM BO BQ BR VG VI HT GY GP GT GF HN GD GL DM DO CA CO CR CU CW MQ MX MS NI KY PA PY PE PR SV BL MF PM VC KN LC SX SR US TT UY FK CL EC JM DZ AO BJ BW BF BI GA GM GH GN GW DJ CD EG ZM ZW CV CM KE KM CI LS LR LY MU MR MG MW ML MA MZ NA NE NG CG RE RW ST SC SN SO SD SL TZ TG TN UG CF TD GQ ER SZ ET ZA SS AQ BV CX HM TF".split(
    " ",
  );

const ALPHA2_BY_ALPHA3 = new Map(
  ALPHA3_CODES.map((alpha3, index) => [alpha3, ALPHA2_CODES[index]]),
);

export function countryAlpha2(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (normalized.length === 2) return normalized;
  return ALPHA2_BY_ALPHA3.get(normalized) ?? normalized;
}

/**
 * One country's name in the active UI language, falling back to whatever the
 * API supplied.
 *
 * `Country.name` in the database carries the Russian labels the reference list
 * was seeded from, so an API-provided name is only a last resort — a visitor
 * reading the public page in English was being shown "Великобритания". Same
 * CLDR data as `localizedCountryOptions`, for the places that render a single
 * name rather than a `<select>`.
 */
export function localizedCountryName(
  code: string,
  uiLocale: string,
  fallback = "",
): string {
  const alpha2 = countryAlpha2(code);
  if (alpha2.length === 2) {
    try {
      const name = new Intl.DisplayNames([uiLocale || "en"], { type: "region" }).of(alpha2);
      if (name && name !== alpha2) return name;
    } catch {
      // Older/limited browsers fall through to the API-provided label.
    }
  }
  return fallback || code;
}

/**
 * Build form-ready country options in the active UI language.
 *
 * Intl.DisplayNames uses the browser's CLDR data, so every supported UI
 * locale gets native country names without maintaining hundreds of gettext
 * entries. Options are also sorted using that locale's collation rules.
 */
export function localizedCountryOptions(
  countries: readonly CountryLike[],
  uiLocale: string,
): LocalizedCountryOption[] {
  const locale = uiLocale || "en";
  let displayNames: Intl.DisplayNames | null = null;

  try {
    displayNames = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    // Older/limited browsers still get the API-provided label.
  }

  const options = countries.map((country) => {
    const alpha3 = country.code.trim().toUpperCase();
    const value = countryAlpha2(alpha3);
    const localizedName =
      displayNames && value.length === 2 ? displayNames.of(value) : undefined;

    return {
      value,
      label: localizedName || country.name,
      alpha3,
    };
  });

  try {
    const collator = new Intl.Collator([locale], { sensitivity: "base" });
    return options.sort((a, b) => collator.compare(a.label, b.label));
  } catch {
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }
}
